// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Otherwise, use the given marketplaceRootURL parameter or the same domain and port as the frontend
  return marketplaceRootURL ? marketplaceRootURL.replace(/\/$/, '') : `${window.location.origin}`;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const stripHtml = str =>
  String(str || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const responseTextPreview = text => stripHtml(text).slice(0, 500);

const readErrorResponse = (res, contentType) => {
  if (contentType === 'application/json') {
    return res.json().catch(() => ({}));
  }

  if (contentType === 'application/transit+json') {
    return res
      .text()
      .then(deserialize)
      .catch(() => ({}));
  }

  return res.text().then(text => {
    const responseText = responseTextPreview(text);

    return {
      error: responseText,
      message: responseText || `${res.status} ${res.statusText}`,
      contentType,
    };
  });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;

  // If headers are not set, we assume that the body should be serialized as transit format.
  const shouldSerializeBody =
    (!headers || headers['Content-Type'] === 'application/transit+json') && body;
  const bodyMaybe = body ? { body: shouldSerializeBody ? serialize(body) : body } : {};

  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit).
    headers: headers || { 'Content-Type': 'application/transit+json' },
    ...bodyMaybe,
    ...rest,
  };

  return window.fetch(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return readErrorResponse(res, contentType).then(data => {
        const message =
          data?.message ||
          data?.error ||
          `Local API request failed: ${res.status} ${res.statusText}`;
        let e = new Error(message);
        e = Object.assign(e, {
          name: data?.name || 'LocalAPIError',
          status: data?.status || res.status,
          statusText: data?.statusText || res.statusText,
          path,
          url,
          ...data,
        });

        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

// Accept a prefix only. Never let a full PAN reach the application's backend.
export const lookupCardBinApi = (bin, { signal } = {}) => {
  if (typeof bin !== 'string' || !/^\d{8}$/.test(bin)) {
    return Promise.reject(new Error('Expected an 8 digit BIN'));
  }
  return post('/api/card-bin/lookup', JSON.stringify({ bin }), {
    headers: { 'Content-Type': 'application/json' },
    signal,
    cache: 'no-store',
  });
};

const get = (path, options = {}) => {
  const requestOptions = {
    method: methods.GET,
    ...options,
  };

  return request(path, requestOptions);
};

const STREAM_CHAT_NOTIFICATIONS_CACHE_TTL_MS = 15 * 1000;
const DEFAULT_STREAM_CHAT_NOTIFICATIONS_CACHE_SCOPE = 'anonymous';
const streamChatNotificationsStateByScope = new Map();

const normalizeStreamChatNotificationsCacheScope = cacheScope => {
  const normalized = String(cacheScope || '').trim();
  return normalized || DEFAULT_STREAM_CHAT_NOTIFICATIONS_CACHE_SCOPE;
};

const getStreamChatNotificationsState = cacheScope => {
  const scope = normalizeStreamChatNotificationsCacheScope(cacheScope);

  if (!streamChatNotificationsStateByScope.has(scope)) {
    streamChatNotificationsStateByScope.set(scope, {
      defaultRequest: null,
      forceRequest: null,
      requestVersion: 0,
      cachedResponse: null,
      cachedAt: 0,
    });
  }

  return streamChatNotificationsStateByScope.get(scope);
};

const getCachedStreamChatNotificationsResponse = cacheScope => {
  const state = getStreamChatNotificationsState(cacheScope);

  if (!state.cachedResponse || !state.cachedAt) {
    return null;
  }

  const ageMs = Date.now() - state.cachedAt;
  return ageMs < STREAM_CHAT_NOTIFICATIONS_CACHE_TTL_MS ? state.cachedResponse : null;
};

const setCachedStreamChatNotificationsResponse = (cacheScope, response) => {
  const state = getStreamChatNotificationsState(cacheScope);
  state.cachedResponse = response;
  state.cachedAt = Date.now();
  return response;
};

export const clearStreamChatNotificationsApiCache = (cacheScope = null) => {
  if (cacheScope == null) {
    streamChatNotificationsStateByScope.clear();
    return;
  }

  streamChatNotificationsStateByScope.delete(
    normalizeStreamChatNotificationsCacheScope(cacheScope)
  );
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

export const sendTransactionMessageApi = body => {
  return post('/api/transaction-message', body);
};

export const submitReviewApi = body => {
  return post('/api/submit-review', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

export const initiaterescheduleBookingApi = body => {
  return post('/api/initiate-reschedule-booking', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

// Mapbox
export const getReverseGeocode = ({ latitude, longitude }) => {
  return get(`/api/google/get-reverse-geocode?latitude=${latitude}&longitude=${longitude}`);
};

// omise apis
export const captureChargeApi = body => {
  return post('/api/capture-charge', body);
};
export const retrieveChargeApi = body => {
  return post('/api/retrieve-charge', body);
};

export const confirmCardPaymentApi = body => {
  return post('/api/confirm-card-payment', body);
};

export const reverseCardChargeApi = body => {
  return post('/api/reverse-card-charge', body);
};

export const createCardRefundApi = body => {
  return post('/api/create-card-refund', body);
};

export const createNonCardRefundApi = body => {
  return post('/api/create-non-card-refund', body);
};

export const createNonCardChargeApi = body => {
  return post('/api/create-non-card-charge', body);
};
export const saveReceiptRequest = body => post('/api/receipt-request', body);

export const createCardChargeApi = body => {
  return post('/api/create-card-charge', body);
};

export const createRecipientApi = body => {
  return post('/api/create-recipient', body);
};

export const retrieveRecipientApi = body => {
  return post('/api/retrieve-recipient', body);
};

export const destroyRecipientApi = body => {
  return post('/api/destory-recipient', body);
};

// Earnings (dashboard) APIs

// GET summary for current provider
export const earningsSummaryApi = options => {
  return get('/api/earnings/summary', options);
};

// GET bookings table rows for current provider
export const earningsBookingsApi = ({ page = 1, perPage = 25 } = {}, options = {}) => {
  return get(`/api/earnings/bookings?page=${page}&perPage=${perPage}`, options);
};

// POST trigger auto payout (manual click)
// Server validates 72h window and aggregates transfers per booking
export const triggerAutoPayoutApi = body => {
  return post('/api/earnings/transfer-available', body);
};

export const streamChatTokenApi = body => {
  return post('/api/stream-chat-token', body);
};
export const openDirectMessageApi = body => {
  return post('/api/open-dm', body);
};
export const sendStreamTransactionMessageApi = body => {
  return post('/api/stream-chat/transaction-message', body);
};
export const uploadStreamChatAttachmentApi = ({ file, txId }) => {
  const fileName = encodeURIComponent(file?.name || 'attachment');

  return post('/api/stream-chat/attachment-upload', file, {
    headers: {
      'Content-Type': file?.type || 'application/octet-stream',
      'X-SceneAccess-File-Name': fileName,
      'X-SceneAccess-Transaction-Id': txId,
    },
  }).then(response => response?.data || response);
};
export const getStreamChatNotificationsApi = ({
  forceFresh = false,
  cacheScope = DEFAULT_STREAM_CHAT_NOTIFICATIONS_CACHE_SCOPE,
} = {}) => {
  const scope = normalizeStreamChatNotificationsCacheScope(cacheScope);
  const state = getStreamChatNotificationsState(scope);

  if (forceFresh) {
    state.cachedResponse = null;
    state.cachedAt = 0;

    if (!state.forceRequest) {
      const requestVersion = state.requestVersion + 1;
      state.requestVersion = requestVersion;

      state.forceRequest = get('/api/stream-chat-notifications?fresh=true')
        .then(response => {
          if (getStreamChatNotificationsState(scope).requestVersion === requestVersion) {
            return setCachedStreamChatNotificationsResponse(scope, response);
          }

          return response;
        })
        .finally(() => {
          getStreamChatNotificationsState(scope).forceRequest = null;
        });
    }

    return state.forceRequest;
  }

  if (state.forceRequest) {
    return state.forceRequest;
  }

  const cachedResponse = getCachedStreamChatNotificationsResponse(scope);
  if (cachedResponse) {
    return Promise.resolve(cachedResponse);
  }

  if (!state.defaultRequest) {
    const requestVersion = state.requestVersion + 1;
    state.requestVersion = requestVersion;

    state.defaultRequest = get('/api/stream-chat-notifications')
      .then(response => {
        if (getStreamChatNotificationsState(scope).requestVersion === requestVersion) {
          return setCachedStreamChatNotificationsResponse(scope, response);
        }

        return response;
      })
      .finally(() => {
        getStreamChatNotificationsState(scope).defaultRequest = null;
      });
  }

  return state.defaultRequest;
};
export const markStreamChatNotificationReadApi = txId => {
  return post(`/api/stream-chat-notifications/${encodeURIComponent(txId)}/read`, {}).finally(() => {
    clearStreamChatNotificationsApiCache();
  });
};
export const markAllStreamChatNotificationsReadApi = () => {
  return post('/api/stream-chat-notifications/mark-all-read', {}).finally(() => {
    clearStreamChatNotificationsApiCache();
  });
};
export const getSubMerchantIdApi = body => {
  return post('/api/get-subMerchant-Id', body);
};

export const checkNonCardChargeStatusAndConfirmApi = body => {
  return post('/api/check-non-card-charge-status-and-confirm', body);
};

export const buildThaiLanguagePatchApi = body => {
  return post('/api/build-thai-language-patch', body);
};

/**
 * Universal translation endpoint for any content type
 * @param {Object} body
 * @param {string} body.transactionId - Transaction UUID
 * @param {string} body.storageKey - Where to store: 'chatTranslations', 'activityFeedTranslations', 'projectInfoTranslations', etc.
 * @param {Array<{id: string, text: string}>} body.items - Items to translate
 */
export const translateTransactionContentApi = body => {
  return post('/api/translate-transaction-content', body);
};

/**
 * Backward compatible endpoint for chat translations
 * @param {Object} body
 * @param {string} body.transactionId - Transaction UUID
 * @param {Array<{id: string, text: string}>} body.messages - Messages to translate
 */
export const translateTransactionChatApi = body => {
  return post('/api/translate-transaction-chat', body);
};

export const generatePaymentReceiptApi = body => {
  return post('/api/generate-payment-receipt', body);
};

export const sendWelcomeGuideMessageApi = body => {
  return post('/api/welcome-guide/send', body);
};

export const previewCustomOfferApi = body => {
  return post('/api/custom-offers/preview', body);
};

export const fetchCustomOfferAvailabilityApi = body => {
  return post('/api/custom-offers/availability', body);
};

export const sendCustomOfferApi = body => {
  return post('/api/custom-offers', body);
};

export const customOfferActionApi = body => {
  return post('/api/custom-offers/action', body);
};

export const rescheduleCustomOfferApi = body => {
  return post('/api/custom-offers/reschedule', body);
};

export const cancelBookingRequestApi = body => {
  return post('/api/booking-requests/cancel', body);
};

export const profileAuthoredReviewsApi = userId => {
  return get(`/api/profile-authored-reviews?userId=${userId}`);
};

export const getVirtualTourStatusesApi = listingIds => {
  return post('/api/virtual-tours/status', { listingIds });
};

export const getVirtualTourApi = listingId => {
  return get(`/api/virtual-tour/${encodeURIComponent(listingId)}`);
};

export const createVirtualTourGrantApi = (listingId, options = {}) => {
  return post(`/api/virtual-tour/${encodeURIComponent(listingId)}/grant`, {}, options);
};

export const renewVirtualTourGrantApi = (listingId, options = {}) => {
  return post(`/api/virtual-tour/${encodeURIComponent(listingId)}/grant/renew`, {}, options);
};

export const getVirtualTourViewerUrl = listingId => {
  return `${apiBaseUrl()}/api/virtual-tour/${encodeURIComponent(listingId)}/viewer`;
};

export const getNotificationsStateApi = () => {
  return get('/api/notifications/state');
};

export const getNotificationsApi = ({
  limit = 20,
  cursor = null,
  reason = null,
  fresh = false,
} = {}) => {
  const params = new URLSearchParams();
  params.set('limit', String(limit));
  if (cursor) {
    params.set('cursor', cursor);
  }
  if (reason) {
    params.set('reason', reason);
  }
  if (fresh) {
    params.set('fresh', 'true');
  }
  const query = params.toString();
  return get(`/api/notifications${query ? `?${query}` : ''}`);
};

export const markNotificationReadApi = notificationId => {
  return post(`/api/notifications/${notificationId}/read`, {});
};

export const markAllNotificationsReadApi = () => {
  return post('/api/notifications/mark-all-read', {});
};

export const getLineStatusApi = () => {
  return get('/api/line/status', { cache: 'no-store' });
};

export const createLineLinkSessionApi = params => {
  return post('/api/line/link-session', params || {});
};

export const getLineLinkSessionStatusApi = sessionId => {
  return get(`/api/line/link-session/${encodeURIComponent(sessionId)}/status`);
};

export const startLineVerificationApi = params => {
  return post('/api/line/start-verification', params || {});
};

export const setLinePromptPreferenceApi = (doNotAskAgain = true) => {
  return post('/api/line/prompt-preference', { doNotAskAgain });
};

export const unlinkLineAccountApi = lineUserId => {
  return post('/api/line/unlink', { lineUserId });
};

export const createLineLinkCodeApi = () => {
  return createLineLinkSessionApi();
};

export const setLineDoNotAskAgainApi = (doNotAskAgain = true) => {
  return setLinePromptPreferenceApi(doNotAskAgain);
};
